// ============================================================
// threatAssessment v2 - the threat codex engine (v8.267.0).
//
// The auspex classifies a target as a COMPOSED TITLE (up to two
// class-words + a tier: DAEMONICA MACHINA EXTREMIS) over a
// composite metric measured against a fixed REFERENCE ATTACKER -
// life pool through the real damage model, damage throughput,
// terror, warp presence, and bulk. Size is one voice of five,
// never the verdict (the archetype table in the spec is the
// contract; weights are tuned until it holds).
//
// Display math only: nothing in combat reads any of this. Pure,
// deterministic, no dice (gotcha 5). The soak facts (soakOf,
// armourSpread) remain from v1 - the header's fact-columns use
// them directly.
// ============================================================
import type { OriginClass } from './originClasses';
import { SIZE_MODS } from './constants';
import { getBodyConfig } from './bodyConfig';
import { IMMUNITY_MAP } from './conditionEligibility';
import { resistancesOf, sourceActive } from './resistances';
import type { Resistance } from './resistances';
import { decodeCond } from './conditions';
import type { Target } from '../types';
import type { Character } from './character/types';

export const TIER_ORDER = ['MINIMA', 'MINORIS', 'MEDIA', 'MAJORIS', 'EXTREMIS', 'TERMINUS', 'ULTIMA'] as const;
export type ThreatTier = (typeof TIER_ORDER)[number];

type ThreatVectors = Record<'ferocitas' | 'duritia' | 'terror' | 'immaterium' | 'corpus', number>;

export interface ThreatAssessment {
  tier: ThreatTier;
  /** The composed class-words, precedence order, max two. */
  origin: string[];
  /** The full designation: origin words + tier. */
  label: string;
  sizeLabel: string;
  fearRating: number;
  vectors: ThreatVectors;
  /** Per-vector one-line derivations - the instrument shows its work. */
  basis: Record<keyof ThreatVectors, string>;
  /** Ranked / composed discriminator chips. */
  epithets: string[];
  /** Decoded words from the ACTIVE immunity rows: what not to waste. */
  immunities: string[];
  /** Rule-backed High Gothic protocol lines - never flavour that lies. */
  counsel: string[];
  /** Ferocitas rests on priors (no parsed weapons) - the ESTIMATE stencil. */
  estimated: boolean;
  /** Weighted total behind the tier, for the codex ladder's in-band fill. */
  total: number;
}

const ROMAN = ['0', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
const roman = (n: number) => ROMAN[Math.max(0, Math.min(10, Math.round(n)))] ?? String(n);
const signed = (v: number) => (v > 0 ? `+${v}` : String(v));
const clamp5 = (n: number) => Math.max(0, Math.min(5, Math.round(n)));

/**
 * The fixed yardstick every target is measured against: a canonical
 * bolter marine. Constants, not configuration - scores must compare
 * across targets, so the ruler never changes length.
 */
const REF = { hitChance: 0.45, damage: 14.5, pen: 4 };

/** Mean of an NdM+K damage expression; null when unparsable. */
function meanOfExpression(expr: string): number | null {
  const m = /^\s*(\d+)d(\d+)\s*([+-]\s*\d+)?\s*$/.exec(expr || '');
  if (!m) return null;
  const bonus = m[3] ? parseInt(m[3].replace(/\s/g, ''), 10) : 0;
  return (parseInt(m[1], 10) * (parseInt(m[2], 10) + 1)) / 2 + bonus;
}

const isCrowd = (t: Target) => !!t.horde?.isHorde;
/** Psy on the Target itself (statblocks can be witches, v8.272.0),
 * the character sheet as fallback for older projections. */
// `||`, not `??`: emptyTarget defaults psyRating to 0, which must not
// mask a sheet's rating on older snapshots that predate the field.
const psyOf = (t: Target, source?: Character | null) => t.psyRating || source?.psyRating || 0;
// A crowd's weight in the threat score (a heuristic, and the codex tier the daemonic look scales by): kept as
// it was when the rule changed to +1 HIT per 10 magnitude (ruling 2026-10-09), so no tier moves.
const attacksPerRound = (t: Target) => (isCrowd(t) ? Math.max(1, Math.floor((t.horde?.magnitude ?? 0) / 10)) : 1);

/** stat + (mult - 1) * bonus * 10 - unnaturals folded into the test value. */
const eff = (stat: number, mult?: number) => stat + ((mult || 1) - 1) * Math.floor(stat / 10) * 10;

/** Mean effective characteristic over what the target actually exposes. */
function averageEffectiveStat(t: Target, source?: Character | null): number {
  if (source?.characteristics) {
    const all = Object.values(source.characteristics);
    return all.reduce((s, c) => s + eff(c.value, c.unnaturalMultiplier), 0) / all.length;
  }
  const known = [
    eff(t.parry?.weaponSkill ?? 0, t.unnaturalWeaponSkill),
    eff(t.dodge?.agility ?? 0, t.unnaturalAgility),
  ];
  return known.reduce((s, v) => s + v, 0) / known.length;
}

/** The middle armour value across this body's locations (vehicles: facings). */
function medianArmour(t: Target): number {
  const config = getBodyConfig(t.bodyType || 'humanoid');
  const keys = config.armourMode === 'facing' ? ['front', 'side', 'rear'] : config.locations.map((l) => l.id);
  const values = keys.map((k) => t.armour?.[k] ?? 0);
  return values.sort((a, b) => a - b)[Math.floor(values.length / 2)] ?? 0;
}

// ── DURITIA: reference hits to drop ──────────────────────────
function scoreDuritia(t: Target): { score: number; basis: string } {
  if (isCrowd(t)) {
    const pool = t.horde?.magnitude ?? 0;
    return { score: band(pool, [2, 5, 12, 30, 80]), basis: `magnitude ${pool} - ~${pool} reference hits` };
  }
  const median = medianArmour(t);
  const soak =
    soakOf(t) +
    Math.max(0, median + (t.naturalArmour || 0) + (t.machineAP || 0) + (t.daemonicProtection || 0) - REF.pen);
  // Floor at 2.5, not 1: even the reference weapon finds gaps (fury,
  // crits, weak points), so heavy plate slows the pool's growth
  // instead of walling it to infinity - a chaos champion is MAJORIS,
  // not an EXTREMIS fortress (the archetype table's ruling).
  const through = Math.max(2.5, REF.damage - soak);
  const dodgeOdds = t.dodge?.canDodge ? Math.min(0.85, eff(t.dodge.agility, t.unnaturalAgility) / 100) : 0;
  const parryOdds = t.parry?.canParry ? Math.min(0.85, eff(t.parry.weaponSkill, t.unnaturalWeaponSkill) / 100) : 0;
  const reaction = 1 / (1 - Math.max(dodgeOdds, parryOdds));
  const regen = Math.min(2, 1 + (t.regeneration || 0) / (REF.hitChance * through));
  const ff = t.forceField?.active
    ? 1 / (1 - ((t.forceField.protectionRating || 0) / 100) * (1 - (t.forceField.overloadThreshold || 0) / 100))
    : 1;
  const pool = ((t.wounds?.max || 0) / through) * reaction * regen * ff;
  return {
    score: band(pool, [2, 5, 12, 30, 80]),
    basis: `${t.wounds?.max || 0} wounds, soak ${soak}, reactions x${reaction.toFixed(1)} - ~${Math.round(pool)} reference hits`,
  };
}

// ── FEROCITAS: expected damage out per round ─────────────────
function scoreFerocitas(t: Target, source?: Character | null): { score: number; basis: string; estimated: boolean } {
  const strength = (t.strengthBonus || 0) * 1;
  const parsed = (source?.weapons ?? [])
    .map((w) => {
      const mean = meanOfExpression(w.damageExpression);
      return mean == null ? null : mean + (w.penetration || 0) / 2;
    })
    .filter((v): v is number => v != null);
  const estimated = parsed.length === 0;
  const weaponMean = estimated ? Math.max(strength + 5.5, 8) : Math.max(...parsed);
  const skill = Math.max(
    eff(t.parry?.weaponSkill ?? 30, t.unnaturalWeaponSkill),
    source?.characteristics ? eff(source.characteristics.bs.value, source.characteristics.bs.unnaturalMultiplier) : 0,
  );
  const attacks = attacksPerRound(t);
  const out = Math.min(0.95, skill / 100) * attacks * weaponMean;
  let score = band(out, [2, 5, 10, 18, 30]);
  // Raw statline quality counts even unarmed.
  if (averageEffectiveStat(t, source) >= 55) score = clamp5(score + 1);
  return {
    score,
    basis: `${estimated ? 'est. ' : ''}${weaponMean.toFixed(1)} dmg x ${attacks} attack${attacks > 1 ? 's' : ''} at ${Math.round(skill)} - ~${out.toFixed(1)}/round`,
    estimated,
  };
}

/** Score a value against ascending thresholds -> 0..5. */
function band(value: number, steps: [number, number, number, number, number]): number {
  let s = 0;
  for (const step of steps) if (value >= step) s++;
  return s;
}

const SIZE_INDEX: Record<string, number> = {
  Minuscule: 0,
  Puny: 0,
  Scrawny: 1,
  Average: 1,
  Hulking: 3,
  Enormous: 4,
  Massive: 5,
};

const isBig = (t: Target) => (SIZE_INDEX[t.size || 'Average'] ?? 2) >= 3;
const isUnarmed = (t: Target, source?: Character | null) =>
  (t.defenderWeapons ?? []).length === 0 && (source?.weapons ?? []).length === 0;

// ── The pantheon: ordered class-words ────────────────────────
const ORIGIN_RULES: Array<[OriginClass, (t: Target, source?: Character | null) => boolean]> = [
  ['DAEMONICA', (t) => (t.daemonicProtection || 0) > 0],
  ['XENOS', (t) => !!t.xenos],
  ['UMBRA', (t) => !!t.fromBeyond || !!t.stuffOfNightmares],
  ['IMMATERIA', (t) => !!t.incorporeal],
  ['EXANIMA', (t) => !!t.undying],
  ['MALEFICA', (t, source) => psyOf(t, source) > 0],
  ['MACHINA', (t) => (t.machineAP || 0) > 0 || t.bodyType === 'vehicle'],
  ['ASTARTES', (t) => !!t.astartes],
  [
    'BESTIA',
    // Inferred, narrowly: big, unarmed, nothing else signalled.
    (t, source) => isBig(t) && isUnarmed(t, source) && psyOf(t, source) === 0,
  ],
];

/** ALL the classes a target belongs to (the designation shows two): MORTALIS when none applies. */
export function originClassesOf(t: Target, source?: Character | null): string[] {
  const matches = ORIGIN_RULES.filter(([, applies]) => applies(t, source)).map(([name]) => name);
  return matches.length > 0 ? matches : ['MORTALIS'];
}

/** Related demoted class-words compose into one chip. */
const COMPOSE_GROUPS = [['UMBRA', 'IMMATERIA', 'EXANIMA']];

function resolveOrigins(t: Target, source?: Character | null): { title: string[]; demoted: string[] } {
  const matches = originClassesOf(t, source);
  return { title: matches.slice(0, 2), demoted: matches.slice(2) };
}

function composeDemoted(demoted: string[]): string[] {
  const out: string[] = [];
  const used = new Set<string>();
  for (const group of COMPOSE_GROUPS) {
    const present = group.filter((g) => demoted.includes(g));
    if (present.length > 1) {
      out.push(present.join(' '));
      present.forEach((p) => used.add(p));
    }
  }
  return [...out, ...demoted.filter((d) => !used.has(d))];
}

// ── The verdict ──────────────────────────────────────────────
const TIER_BANDS: Array<[number, ThreatTier]> = [
  [2, 'MINIMA'],
  [5, 'MINORIS'],
  [8, 'MEDIA'],
  [12, 'MAJORIS'],
  [16, 'EXTREMIS'],
  [20, 'TERMINUS'],
];

/**
 * How far into its band a total sits, 0..1 - the codex ladder fills
 * the NEXT rung by this much, so two MAJORIS are visibly not equals.
 */
export function tierProgress(total: number): number {
  let lower = 0;
  for (const [cap] of TIER_BANDS) {
    if (total <= cap) return Math.max(0, Math.min(1, (total - lower) / (cap - lower)));
    lower = cap;
  }
  return 1;
}

/** The band a total falls in, before the peak and ULTIMA rules. */
function bandTier(total: number): ThreatTier {
  const hit = TIER_BANDS.find(([cap]) => total <= cap);
  return hit ? hit[1] : 'ULTIMA';
}

function tierOf(total: number, vectors: ThreatVectors): ThreatTier {
  const tier = bandTier(total);
  // PEAK rule: a maxed vector reads EXTREMIS at least - except CORPUS;
  // sheer size alone never carries the verdict.
  const peaked = (['ferocitas', 'duritia', 'terror', 'immaterium'] as const).some((k) => vectors[k] >= 5);
  if (peaked && TIER_ORDER.indexOf(tier) < TIER_ORDER.indexOf('EXTREMIS')) return 'EXTREMIS';
  // ULTIMA gate: mythical - a huge total AND excellence on two axes.
  const fives = Object.values(vectors).filter((v) => v >= 5).length;
  return tier === 'ULTIMA' && !(total >= 21 && fives >= 2) ? 'TERMINUS' : tier;
}

// ── Epithets: ranked discriminators ──────────────────────────
const shielded = (t: Target) => !!t.forceField?.active && (t.forceField.protectionRating || 0) > 0;

function buildEpithets(t: Target, demoted: string[], psy: number): string[] {
  const chips: (string | false)[] = [
    (t.regeneration || 0) > 0 && `REGENERATIVE ${roman(t.regeneration)}`,
    psy > 0 && `PSYKER ${roman(psy)}`,
    (t.fearRating || 0) > 0 && `FEAR ${roman(t.fearRating)}`,
    isCrowd(t) && `MASS ${roman(band(t.horde?.magnitude ?? 0, [10, 20, 40, 70, 100]))}`,
    shielded(t) && 'SHIELDED',
    !!t.stuffOfNightmares && 'NIGHTMARE',
  ];
  return [...composeDemoted(demoted), ...chips.filter((c): c is string => !!c)];
}

/** The ACTIVE immunity rows, decoded to words - IMMUNITY_MAP is the
 * single source, so a future trait shows up here automatically. */
function decodeImmunities(target: Target): string[] {
  const fields = target as unknown as Record<string, unknown>;
  const out = new Set<string>();
  for (const src of IMMUNITY_MAP.filter((m) => sourceActive(fields, m))) {
    for (const code of src.conditions) out.add((decodeCond(code) || code).toUpperCase());
  }
  // AND THE VECTORS (v8.346.0): what the target shrugs off at the door.
  // From engine/resistances, the single source the inducers roll through.
  for (const r of resistancesOf(fields)) out.add(resistanceWord(r));
  return [...out];
}

/** An immune vector as a word, a re-roll with ↻, a bonus with its sign. */
function resistanceWord(r: Resistance): string {
  const v = String(r.vector).toUpperCase();
  if (r.mode === 'immune') return v;
  if (r.mode === 'reroll') return `${v} ↻`;
  const value = r.value ?? 0;
  return `${v} ${value >= 0 ? '+' : ''}${value}`;
}

/**
 * KILL COUNSEL - every line cites the mechanic it reflects; nothing
 * here may drift from the combat engine.
 */
function buildCounsel(t: Target): string[] {
  const out: string[] = [];
  // countdown.ts heals `regeneration` wounds at the round tick.
  if ((t.regeneration || 0) > 0) out.push(`KILL CONFIRM REQUIRED - REGENERATES ${t.regeneration}/ROUND`);
  // damageCalc.ts: incorporeal blocks ALL damage except Force / WarpWeapon / Sanctified.
  if (t.incorporeal) out.push('MUNDANE ARMS AVAIL NOTHING - FORCE, WARP OR SANCTIFIED ONLY');
  // IMMUNITY_MAP: stuffOfNightmares ignores bleed, stun, fatigue, pinning.
  if (t.stuffOfNightmares) out.push('NO VITALS - IT DOES NOT BLEED, STUN OR TIRE');
  // IMMUNITY_MAP: undying is immune to the Kill and Dying conditions.
  if (t.undying) out.push('UNDYING - NO KILL CONDITION HOLDS');
  // defenceChain: the field blocks at protectionRating%, collapsing on a
  // roll at or under overloadThreshold.
  if (shielded(t))
    out.push(`FIELD ${t.forceField.protectionRating} - COLLAPSES ON OVERLOAD ${t.forceField.overloadThreshold}`);
  return out;
}

/** The five vectors' scores, with what ferocitas and duritia rest on. */
function measure(target: Target, source?: Character | null) {
  const fer = scoreFerocitas(target, source);
  const dur = scoreDuritia(target);
  const fear = target.fearRating || 0;
  const terror = clamp5(fear + (target.stuffOfNightmares ? 1 : 0) + (fear >= 3 ? 1 : 0));
  const psy = psyOf(target, source);
  const warpMarks = [target.incorporeal, target.fromBeyond, (target.daemonicProtection || 0) > 0].filter(
    Boolean,
  ).length;
  const imm = clamp5(psy + warpMarks);
  const corpus = isCrowd(target)
    ? band(target.horde?.magnitude ?? 0, [10, 20, 40, 70, 100])
    : (SIZE_INDEX[target.size || 'Average'] ?? 2);
  return { fer, dur, terror, psy, imm, corpus };
}

type Measures = ReturnType<typeof measure>;

/** Per-vector one-line derivations - the instrument shows its work. */
function basisOf(target: Target, m: Measures): Record<keyof ThreatVectors, string> {
  const warp = [
    target.incorporeal && ' + incorporeal',
    target.fromBeyond && ' + from beyond',
    (target.daemonicProtection || 0) > 0 && ' + daemonic',
  ];
  return {
    ferocitas: `FEROCITAS ${roman(m.fer.score)} - ${m.fer.basis}`,
    duritia: `DURITIA ${roman(m.dur.score)} - ${m.dur.basis}`,
    terror: `TERROR ${roman(m.terror)} - fear ${target.fearRating || 0}${target.stuffOfNightmares ? ' + nightmare' : ''}`,
    immaterium: `IMMATERIUM ${roman(m.imm)} - psy ${m.psy}${warp.filter(Boolean).join('')}`,
    corpus: isCrowd(target)
      ? `CORPUS ${roman(m.corpus)} - magnitude ${target.horde?.magnitude ?? 0}`
      : `CORPUS ${roman(m.corpus)} - ${String(target.size || 'Average').toUpperCase()}`,
  };
}

function sizeLabelOf(target: Target): string {
  const sizeMod = SIZE_MODS[target.size || 'Average'] || 0;
  return `${String(target.size || 'Average').toUpperCase()}${sizeMod !== 0 ? ` ${signed(sizeMod)}` : ''}`;
}

export function classifyThreat(target: Target, sourceCharacter?: Character | null): ThreatAssessment {
  const { title, demoted } = resolveOrigins(target, sourceCharacter);
  const m = measure(target, sourceCharacter);
  const vectors: ThreatVectors = {
    ferocitas: m.fer.score,
    duritia: m.dur.score,
    terror: m.terror,
    immaterium: m.imm,
    corpus: m.corpus,
  };
  const total = m.fer.score + m.dur.score + 1.2 * m.imm + 0.8 * m.terror + 1.0 * m.corpus;
  const tier = tierOf(total, vectors);
  return {
    tier,
    origin: title,
    label: `${title.join(' ')} ${tier}`,
    sizeLabel: sizeLabelOf(target),
    fearRating: target.fearRating || 0,
    vectors,
    basis: basisOf(target, m),
    epithets: buildEpithets(target, demoted, m.psy),
    immunities: decodeImmunities(target),
    counsel: buildCounsel(target),
    estimated: m.fer.estimated,
    total,
  };
}

/** What every damage roll must beat: TB x unnatural multiplier. */
export function soakOf(target: Target): number {
  return (target.toughnessBonus ?? 0) * (target.unnaturalToughness || 1);
}

/** The armour spread across this body's locations (vehicles: facings). */
export function armourSpread(target: Target): string {
  const config = getBodyConfig(target.bodyType || (target.horde?.isHorde ? 'horde' : 'humanoid'));
  const keys = config.armourMode === 'facing' ? ['front', 'side', 'rear'] : config.locations.map((l) => l.id);
  const values = keys.map((k) => target.armour?.[k] ?? 0);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  return lo === hi ? String(lo) : `${lo}-${hi}`;
}
